import { useState } from 'react';
import { X } from 'lucide-react';
import type { SkillConfig } from '@netsuite-ai/types';
import SkillTestStream, { type TestStep } from './SkillTestStream';
import { type Message } from '../../store/chat.store';

interface Props {
  skillId: string | null;
  config: SkillConfig;
  hideHeader?: boolean;
  onClose?: () => void;
}

export default function SkillTestPanel({
  skillId,
  config,
  hideHeader,
  onClose,
}: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [steps, setSteps] = useState<TestStep[]>([]);

  function handleRun(content: string) {
    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: [{ type: 'text', text: content }],
      status: 'complete',
      toolCallLabel: null,
    };
    const stubReply: Message = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: [
        {
          type: 'text',
          text: `Skill "${config.identity.name || 'Untitled'}" — API not yet wired.`,
        },
      ],
      status: 'complete',
      toolCallLabel: null,
    };
    setMessages((prev) => [...prev, userMsg, stubReply]);

    // Stub execution trace until the test API is wired.
    const grant = config.toolGrants[0];
    setSteps((prev) => [
      ...prev,
      {
        skill: config.identity.name || 'Untitled',
        tool: grant?.toolIdentifier ?? 'no tool granted',
        records: '—',
      },
    ]);
  }

  return (
    <div className="flex h-full flex-col">
      {!hideHeader && (
        <div className="flex h-17.5 w-full items-center justify-between border-b border-neutral-200 px-3">
          <div>
            <p className="text-ink text-sm font-semibold md:text-lg">
              Test panel
            </p>
            {!skillId && (
              <p className="text-xs text-neutral-400">
                Save the skill first to test
              </p>
            )}
          </div>
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close test panel"
              className="-mr-1 rounded p-1 text-neutral-400 hover:text-neutral-600"
            >
              <X size={18} />
            </button>
          )}
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <SkillTestStream
          messages={messages}
          steps={steps}
          onRun={handleRun}
          disabled={!skillId}
        />
      </div>
    </div>
  );
}
